/**
 * Client-side peek at a zones.json (ADR 228) — just the addresses.
 *
 * The server owns parsing and validation (`ZonesJson`); this reads the same file
 * only to answer "what do the addresses in THIS file look like", so the address
 * grammar can be built against real values and its match rate shown before the
 * import commits. Deliberately forgiving: anything it cannot read degrades to an
 * empty list and the import still goes to the server, which reports properly.
 */

/** Both ADR 144 shapes: a bare zone array, or the canonical `{name, zones}` wrapper. */
function zonesOf(root: unknown): unknown[] {
  if (Array.isArray(root)) return root;
  if (root && typeof root === "object") {
    const z = (root as { zones?: unknown }).zones;
    if (Array.isArray(z)) return z;
  }
  return [];
}

/** A zone's `id` IS the storage address (ADR 122/144). */
export function addressesFromZones(root: unknown): string[] {
  const out: string[] = [];
  for (const z of zonesOf(root)) {
    if (!z || typeof z !== "object") continue;
    const id = (z as { id?: unknown }).id;
    if (typeof id === "string" && id.trim() !== "") out.push(id.trim());
  }
  return out;
}

export async function readAddresses(file: File): Promise<string[]> {
  return addressesFromZones(JSON.parse(await file.text()));
}
